import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { I18nProvider } from "./i18n";
import { tg } from "./telegram";
import "./styles.css";

// Tell Telegram we're ready and expand to full screen.
const t = tg();
if (t) {
  t.ready();
  t.expand();
}

/**
 * Telegram WebView caches the Mini App's JS *very* aggressively — sometimes
 * across reloads of the Mini App itself, until the user fully restarts
 * Telegram. nginx already sends Cache-Control: no-store on index.html, but
 * the WebView often ignores it. As a robust fallback, on every page load we
 * fetch /index.html ourselves and compare the script-bundle hash baked into
 * it with the bundle hash that is currently *running*. If they differ, we
 * force a navigation to a fresh URL with a cache-busting query string —
 * which the WebView treats as a brand new resource and will fetch fresh.
 */
function currentBundleHash(): string | null {
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    'script[src*="/assets/index-"][src$=".js"]',
  );
  for (const s of Array.from(scripts)) {
    const m = s.src.match(/\/assets\/index-([A-Za-z0-9_-]+)\.js/);
    if (m) return m[1];
  }
  return null;
}

async function checkForUpdate(): Promise<void> {
  try {
    const res = await fetch("/index.html?_=" + Date.now(), { cache: "no-store" });
    if (!res.ok) return;
    const html = await res.text();
    const m = html.match(/\/assets\/index-([A-Za-z0-9_-]+)\.js/);
    if (!m) return;
    const remote = m[1];
    const local = currentBundleHash();
    if (local && local !== remote) {
      // Brand new URL — guaranteed cache miss for the WebView.
      const next = window.location.pathname + "?_v=" + remote;
      // Use replace so the user can't navigate "back" to the stale page.
      window.location.replace(next);
    }
  } catch {
    // Network failure — keep current build, try again later.
  }
}

// Run once at startup, then every 5 minutes while the Mini App is open.
checkForUpdate();
setInterval(checkForUpdate, 5 * 60 * 1000);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <I18nProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </I18nProvider>
  </React.StrictMode>,
);
